import {
  type FC,
  useEffect,
  useRef,
  useContext,
  type ComponentPropsWithoutRef,
} from "react";
import classNames from "classnames";
import { IntlContext, type TranslationKey } from "../helpers/intlHooks";
import { FormattedMessage } from "react-intl";
import { useIsLandingPageRoute } from "../helpers/skipNavHelpers";
import { updateScreenReaderTitle } from "../helpers/a11yHelpers";

interface FocusableHeaderProps
  extends Omit<ComponentPropsWithoutRef<"h1">, "children"> {
  text:
    | string
    | {
        id: TranslationKey;
        values: ComponentPropsWithoutRef<typeof FormattedMessage>["values"];
      };
  suppressHeaderFocus?: boolean;
  scrollOnFocus?: boolean;
  srOnly?: boolean;
}

export const FocusableHeader: FC<FocusableHeaderProps> = ({
  className = "",
  id,
  suppressHeaderFocus: initialSuppressHeaderFocus,
  scrollOnFocus = false,
  srOnly,
  text,
  ...headingProps
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const fm = useContext(IntlContext).formatMessage;
  const isLandingPageRoute = useIsLandingPageRoute();
  const suppressHeaderFocus = initialSuppressHeaderFocus || isLandingPageRoute;
  const documentTitle =
    typeof text === "string" ? text : fm({ id: text.id }, text.values);

  useEffect(() => {
    if (!suppressHeaderFocus) {
      headingRef.current?.focus({ preventScroll: !scrollOnFocus });
    }
  }, [headingRef, suppressHeaderFocus, scrollOnFocus, text]);

  useEffect(() => {
    const title =
      typeof documentTitle === "string"
        ? documentTitle
        : headingRef.current?.textContent;
    if (title) {
      document.title = title;
      // Update the screen-reader-only live region with the new title if the
      // heading isn't focused
      if (suppressHeaderFocus) {
        updateScreenReaderTitle(title);
      }
    }
  }, [documentTitle, suppressHeaderFocus]);

  return (
    <h1
      ref={headingRef}
      className={classNames(className, {
        "ds-u-visibility--screen-reader": srOnly,
      })}
      tabIndex={-1}
      id={id}
      {...headingProps}
    >
      {typeof text === "string" ? (
        `${text}`
      ) : (
        <>
          <FormattedMessage id={text.id} values={text.values} />
        </>
      )}
    </h1>
  );
};
